import { For, ParentProps, Signal, createContext, createEffect, createMemo, createSignal, on } from 'solid-js'
import { createProxySignal, date, requireContext } from 'common'
import Input from '../st-input/st-input';
import Dropdown from '../st-dropdown/st-dropdown';
import Button from '../st-button/st-button';
import Select from '../st-select/st-select';
import styles from './st-date-picker.module.css'

export class DatePickerProps {
    value!: Signal<Date | undefined>
}

export default function DatePicker(props: ParentProps<DatePickerProps>) {
    let inputRef!: HTMLInputElement;

    const [open, setOpen] = createSignal(false);
    const [value, setValue] = props.value ?? createSignal<Date | undefined>(undefined);
    const valueDay = createMemo(() => value()?.getDate());
    const valueMonth = createMemo(() => value()?.getMonth());
    const valueYear = createMemo(() => value()?.getFullYear());

    const [inputStr, setInputStr] = createSignal('');


    const [pickerMonth, setPickerMonth] = createSignal(new Date().getMonth());
    const [pickerYear, setPickerYear] = createSignal(new Date().getFullYear());

    createEffect(on(() => [value()], () => {
        const currentDateStr = formatDate(value());
        if (inputStr() !== currentDateStr) {
            setInputStr(currentDateStr);
        }

        setPickerMonth(value()?.getMonth() ?? new Date().getMonth());
        setPickerYear(value()?.getFullYear() ?? new Date().getFullYear());
    }))

    createEffect(() => {
        if (open()) {
            setTimeout(() => inputRef.focus(), 5);
        }
    })

    const pickerYearStr = createProxySignal(() => pickerYear().toString(), (val) => setPickerYear(parseInt(val)));
    const pickerMonthStr = createProxySignal(() => pickerMonth().toString(), (val) => setPickerMonth(parseInt(val)));

    const dates = createMemo<Date[]>(() => {
        const days: Date[] = [];

        const firstOfMonth = new Date(pickerYear(), pickerMonth(), 1);
        const dayOfWeek = firstOfMonth.getDay();
        const firstDay = new Date(pickerYear(), pickerMonth(), 1 - dayOfWeek);

        for (let i = 0; i < 42; i++) {
            const day = new Date(firstDay.getFullYear(), firstDay.getMonth(), firstDay.getDate() + i);
            days.push(day);
        }

        return days;
    })

    const changeMonth = (delta: number) => {
        const nextMonth = new Date(pickerYear(), pickerMonth() + delta);
        setPickerYear(nextMonth.getFullYear());
        setPickerMonth(nextMonth.getMonth());
    }

    const selectDay = (date: Date) => {
        setValue(date);
    }

    const onFocusOut = () => {
        const date = parseDateInput(inputStr());
        if (date) {
            setValue(date);
        } else if (inputStr()) {
            // Invalid date, don't let the user leave
            inputRef.focus();
        }
    }

    return (
        <div class={styles['st-date-picker']}>
            <Dropdown open={[open, setOpen]}>
                <Dropdown.Trigger>
                    <Input ref={inputRef} value={[inputStr, setInputStr]} icon='fa fa-calendar-days' onFocusOut={() => onFocusOut()} />
                </Dropdown.Trigger>
                <Dropdown.Content>
                    <div class={styles['picker-panel']}>
                        <Select value={pickerMonthStr}>
                            <For each={Array.from({ length: 12 }, (_, i) => i)}>{(month) => (
                                <Select.Option value={month.toString()}>{date.monthIndexName(month)}</Select.Option>
                            )}</For>
                        </Select>

                        <Select value={pickerYearStr} displayValue={pickerYear()}>
                            <For each={[...Array(79).keys()].map(i => i + 1975)}>{(year) => (
                                <Select.Option value={year.toString()}>{year}</Select.Option>
                            )}</For>
                        </Select>

                    </div>
                    <div class={styles['picker-calendar']}>
                        <Button onClick={() => changeMonth(-1)}>
                            <i class='fa fa-chevron-left'></i>
                        </Button>
                        <div class={styles['picker-days']}>
                            <For each={dates()}>{(day) => (
                                <div
                                    class={styles['picker-day']}
                                    data-disabled={day.getMonth() != pickerMonth()}
                                    data-selected={valueDay() == day.getDate() && valueMonth() == day.getMonth() && valueYear() == day.getFullYear()}
                                    onClick={() => selectDay(day)}
                                >
                                    {day.getDate()}
                                </div>
                            )}</For>
                        </div>
                        <Button onClick={() => changeMonth(+1)}>
                            <i class='fa fa-chevron-right'></i>
                        </Button>
                    </div>
                </Dropdown.Content>
            </Dropdown>
        </div>
    );
}

DatePicker.defaultProps = new DatePickerProps();

export const parseDateInput = (dateStr: string): Date | undefined => {
    if (dateStr.toLowerCase() === 't') return new Date();

    const parts = dateStr.split('/');
    if (parts.length != 3) return undefined;

    const month = parseInt(parts[0]) - 1;
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return undefined;

    return new Date(year, month, day);
}

export const formatDate = (date: Date | undefined): string => {
    if (!date) return '';
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`
}